import {InfiniteData, QueryKey} from '@tanstack/query-core';
import {UseInfiniteQueryOptions, useInfiniteQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import {axiosBffFetch} from '../../../axios/axiosBffFetch';
import {PagewebError} from '../../../errors/PagewebError.class';
import {AskListData, AskListRequestParams, AskListResponse} from '../type';

type PageParam = {current: number; rowsPerPage: number};

const useInfiniteAskListQueryHook = <
  TData = InfiniteData<AskListData | null, PageParam>,
  TError = PagewebError,
>(
  variables: AskListRequestParams,
  options: Omit<
    UseInfiniteQueryOptions<
      AskListData | null,
      TError,
      TData,
      AskListData | null,
      QueryKey,
      PageParam
    >,
    'queryKey'
  > & {
    queryKey?: UseInfiniteQueryOptions<
      AskListData | null,
      TError,
      TData,
      AskListData | null,
      QueryKey,
      PageParam
    >['queryKey'];
  }
) => {
  const {queryKey: optionsQueryKey, ...restOptions} = omit(options, [
    'initialPageParam',
    'getNextPageParam',
  ]);

  return useInfiniteQuery<
    AskListData | null,
    TError,
    TData,
    QueryKey,
    PageParam
  >({
    queryKey: optionsQueryKey ?? useInfiniteAskListQueryHook.getKey(variables),
    queryFn: async ({pageParam}) => {
      const params = new URLSearchParams({
        current: (pageParam?.current ?? variables.current).toString(),
        rowsPerPage: (
          pageParam?.rowsPerPage ?? variables.rowsPerPage
        ).toString(),
        projectId: variables.projectId,
      });

      const result: AxiosResponse<AskListResponse> = await axiosBffFetch(
        `/api/ask/list?${params.toString()}`
      );
      return result.data.data;
    },
    initialPageParam: {
      current: variables.current,
      rowsPerPage: variables.rowsPerPage,
    },
    getNextPageParam: lastPage => {
      if (!lastPage || isNil(lastPage.page.next)) {
        return undefined;
      }
      return {
        current: lastPage.page.next,
        rowsPerPage: variables.rowsPerPage,
      };
    },
    ...restOptions,
  });
};

useInfiniteAskListQueryHook.getKey = (variables: AskListRequestParams) => [
  'askList.infinite',
  variables,
];

export default useInfiniteAskListQueryHook;
